import type { FastchipClusterStockBalanceResponse, FastchipClusterStockSegmentBalance } from '../types/dtos';
import { FASTCHIP_CLUSTER_CP, FASTCHIP_CLUSTER_HB, isDhMarketEstado, isHbCpMarketEstado } from './fastchipClusterRules';

export type FastchipEsimStockContext = {
  clientEstado?: string;
  prCluster?: string;
  hbCpCluster?: string;
};

const ZERO_SEGMENT: FastchipClusterStockSegmentBalance = {
  key: '',
  label: '',
  disponivel: 0,
  total: 0,
  percentualDisponivel: 0,
};

/**
 * Segmento de estoque e-SIM para o contexto da linha em construção.
 * PR: usa breakdown ER/MH e-SIM. Demais mercados: sem segmento e-SIM na API — retorna null (checar pool/API).
 */
export function resolveEsimStockSegment(
  balance: FastchipClusterStockBalanceResponse | null | undefined,
  context: FastchipEsimStockContext
): FastchipClusterStockSegmentBalance | null {
  if (!balance) return null;

  const estado = (context.clientEstado ?? '').trim().toUpperCase();
  if (!estado) return null;

  if (estado === 'PR') {
    const cluster = (context.prCluster ?? 'ER').toUpperCase();
    if (cluster === 'MH') {
      return balance.paranaMhEsim ?? ZERO_SEGMENT;
    }
    return balance.paranaErEsim ?? ZERO_SEGMENT;
  }

  if (estado === 'SC') return null;
  if (estado === 'SP') return null;
  if (isDhMarketEstado(estado)) return null;

  if (isHbCpMarketEstado(estado)) {
    const cluster = (context.hbCpCluster ?? '').toUpperCase();
    if (cluster === FASTCHIP_CLUSTER_HB) return null;
    if (cluster === FASTCHIP_CLUSTER_CP) return null;
    return null;
  }

  // TPCL e demais UFs Fastchip: sem breakdown e-SIM no balanço.
  return null;
}

export function isEsimStockSegmentAvailable(
  segment: FastchipClusterStockSegmentBalance | null | undefined
): boolean {
  return (segment?.disponivel ?? 0) > 0;
}

export function buildEsimUnavailableMessage(context: FastchipEsimStockContext): string {
  const estado = (context.clientEstado ?? '').trim().toUpperCase();
  if (estado === 'PR') {
    const cluster = (context.prCluster ?? 'ER').toUpperCase();
    if (cluster === 'MH') {
      return 'e-SIM não disponível no cluster MH (Paraná) — apenas chip físico.';
    }
    return 'Sem estoque e-SIM no cluster ER (Paraná) para este contrato.';
  }
  if (isHbCpMarketEstado(estado) && context.hbCpCluster) {
    return `Sem estoque e-SIM no cluster ${context.hbCpCluster} para ${estado}.`;
  }
  if (estado) {
    return `Sem estoque e-SIM disponível para ${estado}.`;
  }
  return 'Sem estoque e-SIM para o mercado selecionado.';
}
